import { useLayoutEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Eyebrow, Reveal } from "./Reveal";

const LOOKS = [
  { src: "/images/portrait.jpg", label: "Black", quote: "Real men talk." },
  { src: "/images/city.jpg", label: "Black · Back print", quote: "Keep going." },
  { src: "/images/duo.jpg", label: "Navy & Grey", quote: "Connection is strength.", wide: true },
  { src: "/images/white-model.jpg", label: "White", quote: "Clear head. Open heart." },
  { src: "/images/hero.jpg", label: "Black · Back print", quote: "A stronger tomorrow.", wide: true },
];

const Card = ({ look, i }) => {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
  };
  return (
    <figure data-testid={`lookbook-card-${i}`} onMouseMove={onMove} className={`spotlight group relative shrink-0 overflow-hidden rounded-[24px] bg-[#0B0F15] h-[62vh] md:h-[68vh] ${look.wide ? "aspect-[3/2]" : "aspect-[2/3]"}`}>
      <img src={look.src} alt={`MenTellHealth hoodie — ${look.label}`} className="absolute inset-0 h-full w-full object-cover grayscale-[35%] transition-[filter,transform] duration-700 group-hover:grayscale-0 group-hover:scale-105" />
      <figcaption className="absolute inset-x-0 bottom-0 z-10 p-6 bg-gradient-to-t from-black/85 to-transparent">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-300">0{i + 1} — {look.label}</p>
        <p className="mt-2 font-display font-black uppercase text-2xl sm:text-3xl">{look.quote}</p>
      </figcaption>
    </figure>
  );
};

const Desktop = () => {
  const ref = useRef(null);
  const track = useRef(null);
  const [dist, setDist] = useState(0);
  useLayoutEffect(() => {
    const calc = () => setDist(Math.max(0, (track.current?.scrollWidth ?? 0) - window.innerWidth + 64));
    calc();
    window.addEventListener("resize", calc);
    const t = setTimeout(calc, 800);
    return () => {
      window.removeEventListener("resize", calc);
      clearTimeout(t);
    };
  }, []);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], [0, -dist]);
  return (
    <div ref={ref} style={{ height: `calc(100vh + ${dist}px)` }} className="relative hidden md:block">
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <motion.div ref={track} style={{ x }} className="flex gap-6 pl-8 pr-8 items-center">
          <div className="shrink-0 w-[34vw] pr-8">
            <Eyebrow>04 — Lookbook</Eyebrow>
            <h2 className="mt-5 font-display font-black uppercase text-6xl leading-[0.9] tracking-tight">Worn by<br />men who<br /><span className="text-[var(--mth-blue)]">speak up.</span></h2>
            <p className="mt-6 text-slate-300 max-w-sm">Mountains, city streets, side by side with your mates. Wherever you are, the message goes with you.</p>
          </div>
          {LOOKS.map((l, i) => <Card key={i} look={l} i={i} />)}
        </motion.div>
      </div>
    </div>
  );
};

const Mobile = () => (
  <div className="md:hidden px-5 py-24">
    <Eyebrow>04 — Lookbook</Eyebrow>
    <h2 className="mt-5 mb-10 font-display font-black uppercase text-4xl leading-[0.92] tracking-tight">Worn by men who <span className="text-[var(--mth-blue)]">speak up.</span></h2>
    <div className="flex flex-col gap-5">
      {LOOKS.map((l, i) => (
        <Reveal key={i}>
          <figure className="relative overflow-hidden rounded-[20px] aspect-[4/5]">
            <img src={l.src} alt={`MenTellHealth hoodie — ${l.label}`} className="absolute inset-0 h-full w-full object-cover" />
            <figcaption className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/85 to-transparent">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-300">{l.label}</p>
              <p className="mt-1 font-display font-black uppercase text-2xl">{l.quote}</p>
            </figcaption>
          </figure>
        </Reveal>
      ))}
    </div>
  </div>
);

export const Lookbook = () => (
  <section id="lookbook" data-testid="lookbook-section" className="bg-[#090C11]">
    <Desktop />
    <Mobile />
  </section>
);
